import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';

import { attachClosestEdge, type Edge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { dropTargetForExternal } from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import type { Input } from '@atlaskit/pragmatic-drag-and-drop/types';
import type { CardState, UseCardDragDropParams, UseCardDragDropResult } from '@v2/pages/planboard/components/Board/web/Card/types';

const idleState: CardState = { type: 'idle' };
const draggingState: CardState = { type: 'dragging' };

export const useCardDragDrop = ({
  ref,
  productionOperationId,
  instanceId,
  isRunning,
}: UseCardDragDropParams): UseCardDragDropResult => {
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [state, setState] = useState<CardState>(idleState);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  useEffect(() => {
    const element = ref.current;
    invariant(element);

    const createDropTargetConfig = () => ({
      element,
      canDrop: ({ source }: { source: { data: Record<string, unknown> } }) => {
        return (
          !isRunning && // do not drop onto running jobs
          source.data.instanceId === instanceId &&
          source.data.type === 'card' &&
          source.data.itemId !== productionOperationId
        );
      },
      getIsSticky: () => true,
      getData: ({ input, element }: { input: Input; element: Element }) => {
        const data = { type: 'card', productionOperationId };

        return attachClosestEdge(data, {
          input,
          element,
          allowedEdges: ['top', 'bottom'],
        });
      },
      onDragEnter: ({ source, self }: { source: { data: Record<string, unknown> }; self: { data: Record<string, unknown> } }) => {
        if (source.data.itemId !== productionOperationId) {
          setClosestEdge(extractClosestEdge(self.data));
        }
      },
      onDrag: ({ source, self }: { source: { data: Record<string, unknown> }; self: { data: Record<string, unknown> } }) => {
        if (source.data.itemId !== productionOperationId) {
          setClosestEdge(extractClosestEdge(self.data));
        }
      },
      onDragLeave: () => {
        setClosestEdge(null);
      },
      onDrop: () => {
        setClosestEdge(null);
        if (!ref.current) return;
        setIsAnimating(true);

        const el = ref.current;
        const handle = () => {
          setIsAnimating(false);
          el.removeEventListener('transitionend', handle);
        };
        el.addEventListener('transitionend', handle);
      },
    });

    // Running jobs cannot be dragged - only register as drop target
    if (isRunning) {
      return combine(
        dropTargetForExternal({
          element: element,
        }),
        dropTargetForElements(createDropTargetConfig())
      );
    }

    // Non-running jobs can be dragged and are drop targets
    return combine(
      draggable({
        element: element,
        getInitialData: () => ({ type: 'card', itemId: productionOperationId, instanceId }),
        onGenerateDragPreview: ({ location, source, nativeSetDragImage }) => {
          const sourceElement = source.element;
          const rect = sourceElement.getBoundingClientRect();

          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element,
              input: location.current.input,
            }),
            render({ container }) {
              const previewClone = sourceElement.cloneNode(true) as HTMLElement;
              Object.assign(previewClone.style, {
                boxSizing: 'border-box',
                margin: '0px',
                width: `${rect.width}px`,
                height: `${rect.height}px`,
              });
              container.appendChild(previewClone);
              return () => {
                if (previewClone.parentElement === container) {
                  container.removeChild(previewClone);
                }
              };
            },
          });
        },
        onDragStart: () => setState(draggingState),
        onDrop: () => setState(idleState),
      }),
      dropTargetForExternal({
        element: element,
      }),
      dropTargetForElements(createDropTargetConfig())
    );
  }, [instanceId, isRunning, productionOperationId, ref]);

  return { closestEdge, state, isAnimating };
};
